/**
 * @skewb/env — shared environment-variable validation helpers (plain Zod).
 *
 * Source-only package (no build step): consumed as TypeScript by the Vite apps
 * (works-lite, frontend), which transpile it on the fly. The shared schema
 * helpers live here so the apps and their `vite.config` build-time checks stay
 * in sync without duplicating the validation primitives.
 *
 * Exports:
 *   - validateEnv(schema, raw)      eager parse → typed values, or aggregated throw
 *   - booleanString                 truthy string ('true'|'false'|'1'|'0') → boolean
 *   - booleanStringWithDefault(d)   'true'|'false' → boolean, with a default
 *   - emptyStringAsUndefined(raw)   blank "VAR=" treated as undefined
 */
import { z } from 'zod';

/**
 * Coerces common truthy string representations to a boolean. Mirrors the historic
 * `(process.env.X || '').toLowerCase() === 'true'` semantics used server-side.
 */
export const booleanString = z
  .enum(['true', 'false', '1', '0'])
  .transform((v) => v === 'true' || v === '1');

/**
 * Truthy-string → boolean with a default, matching the client's historic
 * `parseBoolean()` semantics (`'true'|'false'` only).
 */
export const booleanStringWithDefault = (defaultValue: boolean) =>
  z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .default(defaultValue ? 'true' : 'false');

/**
 * Treat empty strings as undefined so a blank `VAR=` in a .env file fails a
 * required check / falls back to its default rather than passing as "". Returns a
 * shallow copy; does not mutate the input.
 */
export function emptyStringAsUndefined(
  raw: Record<string, string | undefined>,
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [key, value] of Object.entries(raw)) {
    out[key] = value === '' ? undefined : value;
  }
  return out;
}

/**
 * Parse `raw` against `schema`, returning typed/coerced values on success.
 *
 * On failure, throws a single Error aggregating every problem — so a missing or
 * malformed variable aborts process startup (backend) or `vite build` (clients)
 * with one actionable list instead of an obscure mid-run failure.
 *
 * @param schema  A Zod object schema (or shape compiled via `z.object`).
 * @param raw     The raw env record (e.g. `process.env` or Vite's `loadEnv()`).
 * @param context Short label for the error header (e.g. "startup", "build").
 * @param hint    Trailing remediation line (e.g. "Check your .env.example.").
 */
export function validateEnv<T extends z.ZodTypeAny>(
  schema: T,
  raw: Record<string, string | undefined>,
  context = 'startup',
  hint = 'Check your environment against the relevant .env',
): z.infer<T> {
  const result = schema.safeParse(emptyStringAsUndefined(raw));
  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');
    throw new Error(`❌ Invalid environment variables (${context} aborted):\n${issues}\n${hint}`);
  }
  return result.data;
}
